import { useCallback, useEffect, useState } from "react"
import { Pencil, Plus, Trash2 } from "lucide-react"
import type { AppShell } from "@/App"
import {
  confirmPricingImport,
  createCarrier,
  createService,
  deleteCarrier,
  deleteService,
  deleteVehicle,
  errorMessage,
  getConfig,
  listCarriers,
  listVehicles,
  listWarehouses,
  patchCarrier,
  patchConfig,
  patchService,
  previewPricingImport,
  putClassPrices,
  quote,
  requoteAll,
  saveVehicle,
  saveWarehouse,
  uploadFile,
  type Carrier,
  type QuoteEvaluation,
  type Service,
  type Vehicle,
  type Warehouse,
} from "@/lib/api"
import { dayShort, dm, eur, kg, m3, n } from "@/lib/format"
import { AsyncButton, ErrorBox, Field, FileButton, inputCls, Loading, Note, Page, Panel, Tag, Td, Th } from "@/components/kit"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

const CLASSES = ["XS", "S", "M", "L", "XL"] as const

function transitText(t: Service["transit_days"]): string {
  if (typeof t === "number") return `${t} dia(s)`
  const parts = Object.entries(t ?? {}).map(([z, d]) => `${z} ${d}`)
  return parts.length ? parts.join(" · ") : "—"
}

export function Fleet({ shell }: { shell: AppShell }) {
  const [carriers, setCarriers] = useState<Carrier[] | null>(null)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [error, setError] = useState<string | null>(null)
  const [editCarrier, setEditCarrier] = useState<Partial<Carrier> | null>(null)
  const [editService, setEditService] = useState<Partial<Service> | null>(null)
  const [editVehicle, setEditVehicle] = useState<Partial<Vehicle> | null>(null)
  const [requoting, setRequoting] = useState<string | null>(null)
  const canEdit = shell.role >= 5

  const load = useCallback(async () => {
    try {
      const [c, v, w] = await Promise.all([listCarriers(shell), listVehicles(shell), listWarehouses(shell)])
      setCarriers(c)
      setVehicles(v)
      setWarehouses(w)
    } catch (err) {
      setError(errorMessage(err))
    }
  }, [shell])
  useEffect(() => void load(), [load])

  /** O tarifário mudou: recalcula opções e data-limite das encomendas em aberto. */
  async function afterTariffChange() {
    await load()
    try {
      const total = await requoteAll(shell, false, (d, t) => setRequoting(`A recalcular encomendas ${d}/${t}…`))
      if (total) shell.toast("success", "Encomendas recalculadas", `${total} em aberto`)
    } catch (err) {
      shell.toast("error", "Recalcular falhou", errorMessage(err))
    } finally {
      setRequoting(null)
    }
  }

  if (error) return <Page><ErrorBox error={error} /></Page>
  if (!carriers) return <Loading />
  const external = carriers.filter((c) => c.kind !== "own_fleet")

  return (
    <Page>
      <Panel
        title="Transportadoras externas"
        subtitle="Tarifário e condicionantes de cada uma: zonas, máximos por volume, trânsito por zona e hora limite de recolha."
        actions={canEdit && (
          <Button size="sm" variant="outline" onClick={() => setEditCarrier({ kind: "external", active: true })}>
            <Plus className="h-3.5 w-3.5" /> Transportadora
          </Button>
        )}
      >
        {requoting && <div className="mb-2 text-sm text-blue-700">{requoting}</div>}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
          {external.map((c) => (
            <div key={c.id} className={cn("rounded-lg border p-3", !c.active && "opacity-60")}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{c.name} <span className="font-mono text-xs text-muted-foreground">{c.code}</span></div>
                  {c.description && <div className="text-xs text-muted-foreground">{c.description}</div>}
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" title="Editar" onClick={() => setEditCarrier(c)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" title="Novo serviço" onClick={() => setEditService({ carrier_id: c.id, active: true, zones: ["continente"], transit_days: { continente: 2 }, accepted_classes: [] })}><Plus className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </div>
              {c.services.length === 0 && <div className="mt-2 text-xs text-muted-foreground">Sem serviços: acrescenta um para entrar na cotação.</div>}
              {c.services.map((s) => (
                <ServiceBlock key={s.id} shell={shell} service={s} canEdit={canEdit} onEdit={() => setEditService(s)} onChanged={afterTariffChange} />
              ))}
            </div>
          ))}
          {external.length === 0 && <div className="text-sm text-muted-foreground">Ainda sem transportadoras. Os valores reais vêm do cliente (§9.1 do plano).</div>}
        </div>
        <Note>
          A data-limite para a transportadora de cada encomenda é o último dia útil em que alguma transportadora ainda chega no prazo. Se a hora limite de recolha de hoje já passou, a recolha conta do dia útil seguinte.
        </Note>
      </Panel>

      <QuoteTester shell={shell} />

      <Panel
        title="Frota própria"
        subtitle={<>No cálculo entra só o <b>custo variável</b> (km, portagens, horas além do turno); o salário do motorista, seguro e amortização são <b>custo fixo do dia</b> e ficam à parte. As rotas chegam na F1.</>}
        actions={canEdit && (
          <Button size="sm" variant="outline" onClick={() => setEditVehicle({ active: true, shift_start: "08:00", shift_end: "18:00", routing_profile: "van" })}>
            <Plus className="h-3.5 w-3.5" /> Veículo
          </Button>
        )}
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {vehicles.map((v) => (
            <div key={v.id} className={cn("rounded-lg border p-3 text-[13px]", !v.active && "opacity-60")}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{v.name}</div>
                  <div className="text-xs text-muted-foreground">{v.kind ?? "—"}</div>
                </div>
                {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditVehicle(v)}><Pencil className="h-3.5 w-3.5" /></Button>}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                <span className="text-muted-foreground">Carga útil</span><span className="font-mono">{kg(v.max_kg)}</span>
                <span className="text-muted-foreground">Volume (85 %)</span><span className="font-mono">{m3(n(v.max_m3))} → {m3((n(v.max_m3) ?? 0) * 0.85)}</span>
                <span className="text-muted-foreground">Plataforma</span><span>{v.has_tail_lift ? <Tag tone="info">sim</Tag> : "não"}</span>
                <span className="text-muted-foreground">Turno</span><span className="font-mono">{v.shift_start}–{v.shift_end}{v.max_drive_min ? ` · condução ≤ ${Math.round(v.max_drive_min / 6) / 10} h` : ""}</span>
                <span className="text-muted-foreground">€/km</span><span className="font-mono">{eur(v.cost_per_km)}</span>
                <span className="text-muted-foreground">Portagens €/km AE</span><span className="font-mono">{eur(v.toll_rate_per_motorway_km)}</span>
                <span className="text-muted-foreground">Hora extra</span><span className="font-mono">{eur(v.cost_per_hour_extra)}</span>
                <span className="text-muted-foreground">Custo fixo do dia</span><span className="font-mono">{eur(v.cost_fixed_day)} <span className="text-[11px] text-muted-foreground">(à parte)</span></span>
              </div>
            </div>
          ))}
          {vehicles.length === 0 && <div className="text-sm text-muted-foreground">Sem veículos. O cliente indica carga, volume, turno e o custo variável e fixo separados (§9.3).</div>}
        </div>
      </Panel>

      <WarehousePanel shell={shell} warehouses={warehouses} canEdit={canEdit} onChanged={load} />
      <RulesPanel shell={shell} />

      {editCarrier && <CarrierDialog shell={shell} carrier={editCarrier} onClose={() => setEditCarrier(null)} onSaved={load} />}
      {editService && <ServiceDialog shell={shell} service={editService} onClose={() => setEditService(null)} onSaved={afterTariffChange} />}
      {editVehicle && <VehicleDialog shell={shell} vehicle={editVehicle} warehouses={warehouses} onClose={() => setEditVehicle(null)} onSaved={load} />}
    </Page>
  )
}

// ─── Serviço ─────────────────────────────────────────────────────────────────

const RULE_LABEL: Record<string, string> = {
  by_weight: "peso da encomenda",
  per_parcel_weight: "peso por volume",
  by_volume: "volume",
  by_dim: "comprimento",
  fixed: "fixo",
  surcharge: "sobretaxa",
}
const RULE_UNIT: Record<string, string> = { by_weight: "kg", per_parcel_weight: "kg", by_volume: "m³", by_dim: "cm" }

/** Escalões do tarifário que não são por classe (ex.: por peso, importados da loja). */
function OtherRules({ rules }: { rules: Service["other_rules"] }) {
  const sorted = [...rules].sort((a, b) =>
    (a.zone ?? "").localeCompare(b.zone ?? "") || a.rule_type.localeCompare(b.rule_type) || (n(a.from_value) ?? 0) - (n(b.from_value) ?? 0))
  const range = (r: Service["other_rules"][number]) => {
    const unit = RULE_UNIT[r.rule_type]
    if (!unit) return "—"
    const from = n(r.from_value) ?? 0
    const to = n(r.to_value)
    return to === null ? `> ${from.toLocaleString("pt-PT")} ${unit}` : `${from.toLocaleString("pt-PT")}–${to.toLocaleString("pt-PT")} ${unit}`
  }
  return (
    <table className="mt-2 w-full border-collapse text-xs">
      <thead>
        <tr>
          <Th>Zona</Th>
          <Th>Por</Th>
          <Th>Escalão</Th>
          <Th className="text-right">Preço</Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r, i) => (
          <tr key={i}>
            <Td className="text-xs">{r.zone ?? "todas"}</Td>
            <Td className="text-xs">{RULE_LABEL[r.rule_type] ?? r.rule_type}</Td>
            <Td className="font-mono text-xs">{range(r)}</Td>
            <Td className="text-right font-mono text-xs">{eur(r.unit_price)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ServiceBlock({ shell, service: s, canEdit, onEdit, onChanged }: { shell: AppShell; service: Service; canEdit: boolean; onEdit: () => void; onChanged: () => Promise<void> }) {
  const zones = Object.keys(s.class_prices).length ? Object.keys(s.class_prices) : ["*"]
  const [editingZone, setEditingZone] = useState<string | null>(null)
  const [prices, setPrices] = useState<Record<string, string>>({})
  const [preview, setPreview] = useState<{ id: string; rules: number; errors: string[] } | null>(null)

  return (
    <div className={cn("mt-3 rounded-md border bg-muted/30 p-2", !s.active && "opacity-60")}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[13px] font-medium">{s.name} <span className="font-mono text-[11px] text-muted-foreground">{s.code}</span></div>
        {canEdit && <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onEdit}><Pencil className="h-3 w-3" /></Button>}
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {s.zones.map((z) => <Tag key={z}>{z}</Tag>)}
        {n(s.max_parcel_kg) !== null && <Tag>≤ {kg(s.max_parcel_kg)}/vol.</Tag>}
        {n(s.max_length_cm) !== null && <Tag>≤ {n(s.max_length_cm)} cm</Tag>}
        {s.pickup_cutoff && <Tag>recolha até {s.pickup_cutoff}</Tag>}
        {s.accepted_classes.length > 0 && <Tag>classes {s.accepted_classes.join(", ")}</Tag>}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">Trânsito: {transitText(s.transit_days)}</div>
      {(Object.keys(s.class_prices).length > 0 || s.other_rules.length === 0) && (
      <table className="mt-2 w-full border-collapse text-xs">
        <thead>
          <tr>
            <Th>Zona</Th>
            {CLASSES.map((c) => <Th key={c} className="text-right">{c}</Th>)}
            <Th />
          </tr>
        </thead>
        <tbody>
          {zones.map((z) => (
            <tr key={z}>
              <Td className="text-xs">{z === "*" ? "todas" : z}</Td>
              {CLASSES.map((c) => (
                <Td key={c} className="text-right font-mono text-xs">
                  {editingZone === z ? (
                    <input className={cn(inputCls, "h-7 w-16 text-right")} value={prices[c] ?? ""} onChange={(e) => setPrices({ ...prices, [c]: e.target.value })} />
                  ) : s.class_prices[z]?.[c] != null ? (
                    eur(s.class_prices[z][c])
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </Td>
              ))}
              <Td className="text-right">
                {canEdit && (editingZone === z ? (
                  <AsyncButton
                    size="sm"
                    className="h-7"
                    onRun={async () => {
                      try {
                        const values = Object.fromEntries(CLASSES.map((c) => [c, prices[c]?.trim() ? Number(prices[c].replace(",", ".")) : null]))
                        await putClassPrices(shell, s.id, z === "*" ? null : z, values)
                        setEditingZone(null)
                        shell.toast("success", "Preços gravados", "Nova versão do tarifário")
                        await onChanged()
                      } catch (e) {
                        shell.toast("error", "Não foi possível gravar", errorMessage(e))
                      }
                    }}
                  >
                    Gravar
                  </AsyncButton>
                ) : (
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => {
                    setEditingZone(z)
                    setPrices(Object.fromEntries(CLASSES.map((c) => [c, s.class_prices[z]?.[c] != null ? String(s.class_prices[z][c]) : ""])))
                  }}>Editar</Button>
                ))}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
      {s.other_rules.length > 0 && <OtherRules rules={s.other_rules} />}
      {canEdit && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <FileButton
            size="sm"
            variant="outline"
            className="h-7"
            accept=".xlsx,.xls,.csv"
            onFile={async (file) => {
              try {
                const key = await uploadFile(shell, file)
                const p = await previewPricingImport(shell, key, s.id)
                setPreview({ id: p.id, rules: p.summary.rules_parsed, errors: p.summary.errors })
              } catch (e) {
                shell.toast("error", "Tarifário não lido", errorMessage(e))
              }
            }}
          >
            Importar tarifário (Excel)
          </FileButton>
          {preview && (
            <>
              <span className="text-xs">{preview.rules} linha(s) lidas{preview.errors.length ? `, ${preview.errors.length} com erro` : ""}.</span>
              <AsyncButton size="sm" className="h-7" onRun={async () => {
                try {
                  const r = await confirmPricingImport(shell, preview.id)
                  setPreview(null)
                  shell.toast("success", `Tarifário v${r.version} aplicado`, `${r.created} linhas`)
                  await onChanged()
                } catch (e) {
                  shell.toast("error", "Não foi possível aplicar", errorMessage(e))
                }
              }}>Aplicar (substitui o atual)</AsyncButton>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => setPreview(null)}>Descartar</Button>
            </>
          )}
        </div>
      )}
      {preview && preview.errors.length > 0 && <div className="mt-1 space-y-0.5 text-[11px] text-amber-800">{preview.errors.slice(0, 5).map((e) => <div key={e}>{e}</div>)}</div>}
    </div>
  )
}

// ─── Teste de cotação ────────────────────────────────────────────────────────

function QuoteTester({ shell }: { shell: AppShell }) {
  const [cp, setCp] = useState("")
  const [lines, setLines] = useState("")
  const [due, setDue] = useState("")
  const [result, setResult] = useState<QuoteEvaluation | null>(null)
  return (
    <Panel title="Testar uma cotação" subtitle="Código postal + artigos (SKU×quantidade, separados por vírgula). Usa os pesos de «Produtos (carga)».">
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Código postal"><input className={cn(inputCls, "w-32")} placeholder="2410-123" value={cp} onChange={(e) => setCp(e.target.value)} /></Field>
        <Field label="Artigos" className="min-w-64 flex-1"><input className={inputCls} placeholder="MLR-9KG×1, FER-2400×2" value={lines} onChange={(e) => setLines(e.target.value)} /></Field>
        <Field label="Prazo (opcional)"><input type="date" className={cn(inputCls, "w-40")} value={due} onChange={(e) => setDue(e.target.value)} /></Field>
        <AsyncButton size="sm" onRun={async () => {
          try {
            const parsed = lines.split(",").map((s) => s.trim()).filter(Boolean).map((s) => {
              const [sku, qty] = s.split(/[×x*]/i).map((p) => p.trim())
              return { sku, qty: Number(qty ?? 1) || 1 }
            })
            setResult(await quote(shell, { postal_code: cp, lines: parsed, due_date: due || undefined }))
          } catch (e) {
            shell.toast("error", "Cotação falhou", errorMessage(e))
          }
        }}>Cotar</AsyncButton>
      </div>
      {result && (
        <div className="mt-3 text-sm">
          <div className="mb-2 flex flex-wrap gap-3 text-[13px]">
            <span>Zona <b>{result.zone ?? "—"}</b>{result.in_fleet_area && " · área da frota"}</span>
            <span>Carga <b className="font-mono">{result.load.load_class ?? "?"} · {kg(result.load.total_kg)} · {m3(result.load.total_m3)}</b></span>
            <span>Prazo <b className="font-mono">{dm(result.due_date)}</b></span>
            <span>Transportadora até <b className="font-mono">{result.carrier_deadline ? dm(result.carrier_deadline) : "— sem reserva"}</b></span>
            {result.load.unknown_skus.length > 0 && <Tag tone="bad">sem peso: {result.load.unknown_skus.join(", ")}</Tag>}
          </div>
          <table className="w-full border-collapse">
            <thead><tr><Th>Transportadora</Th><Th>Custo</Th><Th>Recolha</Th><Th>Chega</Th><Th>Prazo</Th></tr></thead>
            <tbody>
              {result.options.map((x) => (
                <tr key={x.service_id}>
                  <Td>{x.carrier_name}</Td>
                  <Td className="font-mono text-xs">{eur(x.cost)}</Td>
                  <Td className="font-mono text-xs">{dayShort(x.pickup)}{x.missed_cutoff && <span className="text-amber-700"> · hora limite passou</span>}</Td>
                  <Td className="font-mono text-xs">{dayShort(x.arrival)}</Td>
                  <Td>{x.ok ? <Tag tone="ok">cumpre</Tag> : <span className="text-xs text-muted-foreground">{x.why}</span>}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}

// ─── Armazém ─────────────────────────────────────────────────────────────────

function WarehousePanel({ shell, warehouses, canEdit, onChanged }: { shell: AppShell; warehouses: Warehouse[]; canEdit: boolean; onChanged: () => Promise<void> }) {
  const w = warehouses[0]
  const [f, setF] = useState({ code: w?.code ?? "BAJ", name: w?.name ?? "Armazém Bajouca", lat: w?.lat != null ? String(w.lat) : "", lng: w?.lng != null ? String(w.lng) : "" })
  useEffect(() => {
    if (w) setF({ code: w.code, name: w.name, lat: w.lat != null ? String(w.lat) : "", lng: w.lng != null ? String(w.lng) : "" })
  }, [w])
  return (
    <Panel title="Armazém" subtitle="Ponto de partida e regresso das rotas (F1).">
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Código"><input className={cn(inputCls, "w-24")} value={f.code} disabled={!canEdit} onChange={(e) => setF({ ...f, code: e.target.value })} /></Field>
        <Field label="Nome"><input className={cn(inputCls, "w-56")} value={f.name} disabled={!canEdit} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Latitude"><input className={cn(inputCls, "w-28")} value={f.lat} disabled={!canEdit} onChange={(e) => setF({ ...f, lat: e.target.value })} /></Field>
        <Field label="Longitude"><input className={cn(inputCls, "w-28")} value={f.lng} disabled={!canEdit} onChange={(e) => setF({ ...f, lng: e.target.value })} /></Field>
        {canEdit && (
          <AsyncButton size="sm" onRun={async () => {
            try {
              await saveWarehouse(shell, w?.id ?? null, { code: f.code, name: f.name, lat: f.lat || null, lng: f.lng || null, active: true })
              shell.toast("success", "Armazém gravado")
              await onChanged()
            } catch (e) {
              shell.toast("error", "Não foi possível gravar", errorMessage(e))
            }
          }}>Gravar</AsyncButton>
        )}
      </div>
    </Panel>
  )
}

// ─── Regras (settings) ───────────────────────────────────────────────────────

const RULE_LABELS: Array<[string, string]> = [
  ["orders.ready_status", "Estado(s) da loja que contam como prontos (vírgula entre vários)"],
  ["orders.store_max_age_days", "Importar da loja: só os últimos N dias (0 = todos)"],
  ["sla_days", "Prazo de entrega (dias úteis)"],
  ["fleet.service_area", "Área da frota própria (CP4)"],
  ["fleet.horizon_days", "Horizonte de planeamento (dias úteis)"],
  ["fleet.cost_model", "Custo da frota no cálculo"],
  ["fleet.toll_estimate", "Portagens"],
  ["carrier.cutoff_rule", "Hora limite de recolha"],
  ["fleet.m3_headroom_pct", "Volume útil dos veículos (%)"],
  ["fleet.tail_lift_rule", "Plataforma elevatória"],
  ["fleet.service_min_by_class", "Descarga por paragem (min)"],
  ["cargo.class_limits", "Classes de carga"],
  ["cargo.product_dims_margin_pct", "Margem de embalagem nas medidas do aparelho (%)"],
  ["cargo.missing_weight_kg", "Peso assumido por unidade sem peso (kg; 0 = bloqueia)"],
]

function RulesPanel({ shell }: { shell: AppShell }) {
  const [cfg, setCfg] = useState<Awaited<ReturnType<typeof getConfig>> | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [err, setErr] = useState<string | null>(null)
  const isAdmin = shell.role >= 10
  const load = useCallback(async () => {
    try {
      const c = await getConfig(shell)
      setCfg(c)
      setDraft(Object.fromEntries(RULE_LABELS.map(([k]) => {
        const v = c.config[k] ?? c.schema[k]?.default
        return [k, typeof v === "object" ? JSON.stringify(v) : v == null ? "" : String(v)]
      })))
    } catch (e) {
      setErr(errorMessage(e))
    }
  }, [shell])
  useEffect(() => void load(), [load])
  if (err) return <Panel title="Regras"><ErrorBox error={err} /></Panel>
  if (!cfg) return null
  return (
    <Panel
      title="Regras"
      subtitle={isAdmin ? "Settings da instalação. Mudar o prazo, as classes ou a área pede «Recalcular» nas Encomendas." : "Só um administrador altera estas regras."}
      actions={isAdmin && !cfg.readonly && (
        <AsyncButton size="sm" onRun={async () => {
          try {
            const values: Record<string, unknown> = {}
            for (const [k] of RULE_LABELS) {
              const type = cfg.schema[k]?.type
              const raw = draft[k]
              if (raw === "") continue
              values[k] = type === "object" ? JSON.parse(raw) : type === "number" ? Number(raw.replace(",", ".")) : raw
            }
            await patchConfig(shell, values)
            shell.toast("success", "Regras gravadas")
            await load()
          } catch (e) {
            shell.toast("error", "Não foi possível gravar", e instanceof SyntaxError ? "JSON inválido num dos campos" : errorMessage(e))
          }
        }}>Gravar regras</AsyncButton>
      )}
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {RULE_LABELS.map(([k, label]) => (
          <Field key={k} label={label} hint={cfg.schema[k]?.description}>
            <input className={cn(inputCls, cfg.schema[k]?.type === "object" && "font-mono text-xs")} disabled={!isAdmin || cfg.readonly} value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
          </Field>
        ))}
      </div>
      <Note>
        Entrega à porta, encomenda completa (mesmo veículo ou envio, mesmo dia). A escolha é a opção mais barata que cumpre o prazo; o planeador pode sempre trocar. Replaneamento diário e janelas horárias entram na proposta de rotas (F1).
      </Note>
    </Panel>
  )
}

// ─── Diálogos ────────────────────────────────────────────────────────────────

function useForm<T extends Record<string, unknown>>(initial: T) {
  const [f, setF] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.entries(initial).map(([k, v]) => [k, v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v)])),
  )
  const bind = (k: string) => ({ value: f[k] ?? "", onChange: (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value })) })
  return { f, setF, bind }
}

function CarrierDialog({ shell, carrier, onClose, onSaved }: { shell: AppShell; carrier: Partial<Carrier>; onClose: () => void; onSaved: () => Promise<void> }) {
  const { f, bind, setF } = useForm({ code: carrier.code, name: carrier.name, description: carrier.description, active: carrier.active ?? true })
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{carrier.id ? "Transportadora" : "Nova transportadora"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Código"><input className={inputCls} {...bind("code")} /></Field>
          <Field label="Nome"><input className={inputCls} {...bind("name")} /></Field>
          <Field label="Descrição" className="col-span-2"><input className={inputCls} {...bind("description")} /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active === "true"} onChange={(e) => setF({ ...f, active: String(e.target.checked) })} /> Ativa</label>
        </div>
        <div className="flex justify-between gap-2">
          {carrier.id ? (
            <AsyncButton variant="ghost" size="sm" className="text-red-700" onRun={async () => {
              if (!confirm("Apagar a transportadora, os serviços e o tarifário?")) return
              await deleteCarrier(shell, carrier.id!)
              await onSaved()
              onClose()
            }}><Trash2 className="h-3.5 w-3.5" /> Apagar</AsyncButton>
          ) : <span />}
          <AsyncButton size="sm" onRun={async () => {
            try {
              const body = { code: f.code, name: f.name, description: f.description || null, active: f.active === "true", kind: "external" }
              if (carrier.id) await patchCarrier(shell, carrier.id, body)
              else await createCarrier(shell, body)
              await onSaved()
              onClose()
            } catch (e) {
              shell.toast("error", "Não foi possível gravar", errorMessage(e))
            }
          }}>Gravar</AsyncButton>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ServiceDialog({ shell, service, onClose, onSaved }: { shell: AppShell; service: Partial<Service>; onClose: () => void; onSaved: () => Promise<void> }) {
  const { f, bind, setF } = useForm({
    code: service.code ?? "std",
    name: service.name ?? "Normal",
    zones: (service.zones ?? ["continente"]).join(", "),
    transit_days: service.transit_days ?? { continente: 2 },
    pickup_cutoff: service.pickup_cutoff,
    accepted_classes: (service.accepted_classes ?? []).join(", "),
    max_parcel_kg: service.max_parcel_kg,
    max_length_cm: service.max_length_cm,
    active: service.active ?? true,
  })
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{service.id ? "Serviço" : "Novo serviço"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Código"><input className={inputCls} {...bind("code")} /></Field>
          <Field label="Nome"><input className={inputCls} {...bind("name")} /></Field>
          <Field label="Zonas" className="col-span-2" hint="continente, ilhas, madeira, acores, CP4 (4100) ou intervalos (4000-4999); «!4100» exclui">
            <input className={inputCls} {...bind("zones")} />
          </Field>
          <Field label="Dias úteis de trânsito" className="col-span-2" hint='Por zona: {"continente":2,"ilhas":3} — ou um número'>
            <input className={cn(inputCls, "font-mono text-xs")} {...bind("transit_days")} />
          </Field>
          <Field label="Hora limite de recolha"><input type="time" className={inputCls} {...bind("pickup_cutoff")} /></Field>
          <Field label="Classes aceites" hint="vazio = as que têm preço"><input className={inputCls} placeholder="XS, S, M" {...bind("accepted_classes")} /></Field>
          <Field label="Peso máximo por volume (kg)"><input className={inputCls} {...bind("max_parcel_kg")} /></Field>
          <Field label="Comprimento máximo (cm)"><input className={inputCls} {...bind("max_length_cm")} /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active === "true"} onChange={(e) => setF({ ...f, active: String(e.target.checked) })} /> Ativo</label>
        </div>
        <div className="flex justify-between gap-2">
          {service.id ? (
            <AsyncButton variant="ghost" size="sm" className="text-red-700" onRun={async () => {
              if (!confirm("Apagar o serviço e o tarifário dele?")) return
              await deleteService(shell, service.id!)
              await onSaved()
              onClose()
            }}><Trash2 className="h-3.5 w-3.5" /> Apagar</AsyncButton>
          ) : <span />}
          <AsyncButton size="sm" onRun={async () => {
            try {
              const transit = /^\d+$/.test(f.transit_days.trim()) ? Number(f.transit_days) : JSON.parse(f.transit_days || "{}")
              const body = {
                carrier_id: service.carrier_id,
                code: f.code,
                name: f.name,
                zones: f.zones,
                transit_days: transit,
                pickup_cutoff: f.pickup_cutoff || null,
                accepted_classes: f.accepted_classes,
                max_parcel_kg: f.max_parcel_kg || null,
                max_length_cm: f.max_length_cm || null,
                active: f.active === "true",
              }
              if (service.id) await patchService(shell, service.id, body)
              else await createService(shell, body)
              await onSaved()
              onClose()
            } catch (e) {
              shell.toast("error", "Não foi possível gravar", e instanceof SyntaxError ? "Dias de trânsito: JSON inválido" : errorMessage(e))
            }
          }}>Gravar</AsyncButton>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function VehicleDialog({ shell, vehicle, warehouses, onClose, onSaved }: { shell: AppShell; vehicle: Partial<Vehicle>; warehouses: Warehouse[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const { f, bind, setF } = useForm({
    name: vehicle.name, kind: vehicle.kind, max_kg: vehicle.max_kg, max_m3: vehicle.max_m3, has_tail_lift: vehicle.has_tail_lift ?? false,
    cost_per_km: vehicle.cost_per_km, toll_rate_per_motorway_km: vehicle.toll_rate_per_motorway_km, cost_per_hour_extra: vehicle.cost_per_hour_extra,
    cost_fixed_day: vehicle.cost_fixed_day, shift_start: vehicle.shift_start ?? "08:00", shift_end: vehicle.shift_end ?? "18:00",
    max_drive_min: vehicle.max_drive_min, routing_profile: vehicle.routing_profile ?? "van", warehouse_id: vehicle.warehouse_id ?? "", active: vehicle.active ?? true,
  })
  const num = (k: string) => (f[k] === "" ? null : f[k])
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{vehicle.id ? "Veículo" : "Novo veículo"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome"><input className={inputCls} {...bind("name")} /></Field>
          <Field label="Tipo"><input className={inputCls} placeholder="Furgão 3,5 t" {...bind("kind")} /></Field>
          <Field label="Carga útil (kg)"><input className={inputCls} {...bind("max_kg")} /></Field>
          <Field label="Volume (m³)"><input className={inputCls} {...bind("max_m3")} /></Field>
          <Field label="€/km (combustível + desgaste)"><input className={inputCls} {...bind("cost_per_km")} /></Field>
          <Field label="Portagens €/km de autoestrada"><input className={inputCls} {...bind("toll_rate_per_motorway_km")} /></Field>
          <Field label="€/hora além do turno"><input className={inputCls} {...bind("cost_per_hour_extra")} /></Field>
          <Field label="Custo fixo do dia (€)" hint="Salário, seguro, amortização: nunca entra por encomenda"><input className={inputCls} {...bind("cost_fixed_day")} /></Field>
          <Field label="Início do turno"><input type="time" className={inputCls} {...bind("shift_start")} /></Field>
          <Field label="Fim do turno"><input type="time" className={inputCls} {...bind("shift_end")} /></Field>
          <Field label="Condução máxima (min)" hint="Camião: tacógrafo"><input className={inputCls} {...bind("max_drive_min")} /></Field>
          <Field label="Perfil de rota">
            <select className={inputCls} {...bind("routing_profile")}>
              <option value="car">ligeiro</option>
              <option value="van">carrinha</option>
              <option value="truck">camião</option>
            </select>
          </Field>
          {warehouses.length > 1 && (
            <Field label="Armazém (base)" hint="De onde sai e para onde regressa; vazio = o armazém por omissão">
              <select className={inputCls} {...bind("warehouse_id")}>
                <option value="">armazém por omissão</option>
                {warehouses.filter((w) => w.active !== false).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
          )}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.has_tail_lift === "true"} onChange={(e) => setF({ ...f, has_tail_lift: String(e.target.checked) })} /> Plataforma elevatória</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active === "true"} onChange={(e) => setF({ ...f, active: String(e.target.checked) })} /> Ativo</label>
        </div>
        <div className="flex justify-between gap-2">
          {vehicle.id ? (
            <AsyncButton variant="ghost" size="sm" className="text-red-700" onRun={async () => {
              if (!confirm("Apagar o veículo?")) return
              await deleteVehicle(shell, vehicle.id!)
              await onSaved()
              onClose()
            }}><Trash2 className="h-3.5 w-3.5" /> Apagar</AsyncButton>
          ) : <span />}
          <AsyncButton size="sm" onRun={async () => {
            try {
              await saveVehicle(shell, vehicle.id ?? null, {
                name: f.name, kind: f.kind || null, max_kg: num("max_kg") ?? 0, max_m3: num("max_m3") ?? 0, has_tail_lift: f.has_tail_lift === "true",
                cost_per_km: num("cost_per_km") ?? 0, toll_rate_per_motorway_km: num("toll_rate_per_motorway_km") ?? 0,
                cost_per_hour_extra: num("cost_per_hour_extra") ?? 0, cost_fixed_day: num("cost_fixed_day") ?? 0,
                shift_start: f.shift_start, shift_end: f.shift_end, max_drive_min: num("max_drive_min"), routing_profile: f.routing_profile,
                warehouse_id: f.warehouse_id || null, active: f.active === "true",
              })
              await onSaved()
              onClose()
            } catch (e) {
              shell.toast("error", "Não foi possível gravar", errorMessage(e))
            }
          }}>Gravar</AsyncButton>
        </div>
      </DialogContent>
    </Dialog>
  )
}
